/**
 * Unsent reply drafts, kept per ticket.
 *
 * The composer is remounted whenever the agent changes ticket -- deliberately,
 * so a draft can never be carried into the wrong customer's thread -- which
 * meant half a written reply was lost the moment somebody went to check another
 * ticket for the answer. That is the exact thing an agent does mid-reply, so it
 * happened constantly.
 *
 * localStorage rather than the server: a draft is private to the person writing
 * it and worthless to anybody else, syncing it would mean deciding what happens
 * when the same agent has two tabs open on one ticket, and losing them on a
 * machine change is a far smaller cost than either.
 *
 * That choice is what the Drafts view in the sidebar is a view *of* -- this
 * browser's store, listed, rather than a folder on the server. It is worth
 * being plain about the consequence: a draft written on the office PC is not
 * there on a laptop, and clearing site data clears them. What it buys is that
 * the feature needs no table, no sync rules and no answer to "which tab wins",
 * and the thing agents actually lose drafts to -- clicking away -- is fixed
 * completely either way.
 *
 * Two kinds live here. A reply draft belongs to a ticket and is keyed by it; an
 * unsent *new* ticket belongs to nothing yet and is kept on its own at the
 * bottom of this file.
 */

import { useMemo, useSyncExternalStore } from "react";

const KEY = "helpdesk.drafts";

/**
 * How long a draft outlives its last edit.
 *
 * Long enough to survive a lunch break and a browser restart; short enough that
 * a reply abandoned a fortnight ago does not reappear under a conversation that
 * has moved on without it.
 */
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

/** Drafts kept at once. A bound on the store, not a workflow limit. */
const MAX_DRAFTS = 50;

export interface Draft {
  html: string;
  /** Empty unless the agent opened CC and added somebody. */
  cc: { email: string; name?: string }[];
  /**
   * Files already uploaded and waiting to be sent with the reply.
   *
   * Safe to keep here because these are references, not bytes: the upload has
   * already happened and the storage key names an object on the server, so a
   * restored draft points at the same files rather than at anything this
   * browser would have to still be holding. Losing them was the reported
   * problem -- an agent who attached three invoices, went to check the order
   * number on another ticket, and came back to an empty composer.
   */
  uploads: {
    storageKey: string;
    filename: string;
    mimeType: string;
    sizeBytes: number;
  }[];
  /**
   * Files the forward is passing on, as ids off the message it came from.
   *
   * Kept for the same reason `forwardTo` is: a forward restored without them
   * still looks like a forward and sends without the invoice it was about.
   * Ids and names only -- these are not uploads and there is nothing of this
   * browser's in them.
   */
  carried: {
    id: number;
    messageId: number;
    filename: string;
    mimeType: string;
    sizeBytes: number | null;
    isInline: boolean;
  }[];
  mode: "reply" | "note";
  /**
   * Who a forward is addressed to, or null when this is an ordinary reply.
   *
   * Kept with the body, and it has to be. The first version left it out on the
   * grounds that a stale forward pointed at a half-forgotten address is
   * dangerous -- but dropping it is worse: the quoted "Forwarded message" body
   * is restored either way, and without its recipients that draft silently
   * becomes a reply, so pressing send mails the customer something written for
   * somebody else entirely.
   *
   * What makes keeping it safe is that the composer shows the forward banner
   * whenever this is set, naming the recipients above the box. The risk was
   * never the address being remembered; it was the address being remembered
   * invisibly.
   */
  forwardTo?: { email: string; name?: string }[] | null;
  /**
   * A reply's To line, when the agent changed it. Null is "whoever wrote
   * last", which is nearly always.
   *
   * Kept for `forwardTo`'s reason: it decides who pressing send mails, and a
   * draft that came back without it would answer the colleague who forwarded
   * the email in rather than the customer the agent had put there.
   */
  replyTo?: { email: string; name?: string }[] | null;
  /**
   * The thread this reply will quote, built when Reply was chosen.
   *
   * Kept for the same reason `forwardTo` is: it is part of what pressing send
   * will do, and a draft restored without it would go out quoting nothing after
   * the agent had asked for a quote. It is not part of `html` because it is not
   * part of what they are writing -- see the composer, which appends it at send
   * the way the signature is appended.
   *
   * Dropped along with everything else when the body is empty and nothing is
   * attached. A quote with no message above it is not a draft; it is a Reply
   * somebody clicked and thought better of.
   */
  quotedHtml?: string | null;
  /**
   * An overridden Subject header, or null to let the send path derive one.
   *
   * Null and "the default, typed out again" are different states and both are
   * kept as they are: a draft that opened the subject row and left it alone
   * comes back with the row open, because closing it for them would silently
   * discard a decision they had made about the header.
   */
  subject?: string | null;
  /**
   * Enough about the ticket to name this draft without asking the server.
   *
   * Written alongside the body so the drafts list is answerable from this
   * browser alone: a list that had to fetch fifty tickets to render fifty rows
   * would be slower than the queue it sits beside, and would show nothing at
   * all while the drafts it is holding are the one thing it definitely knows.
   *
   * Optional because drafts written before it existed are in real browsers
   * now, and a draft with no context is still a draft -- it lists as its
   * ticket number, which is enough to click.
   */
  context?: { subject?: string; contact?: string };
  savedAt: number;
}

type Store = Record<string, Draft>;

function read(): Store {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") return {};
    return parsed as Store;
  } catch {
    return {};
  }
}

function write(store: Store): void {
  try {
    window.localStorage.setItem(KEY, JSON.stringify(store));
  } catch {
    // A full or disabled localStorage must not break the composer. Losing the
    // draft is the status quo this exists to improve on, not a regression.
  }
}

/**
 * Drop what has aged out, and the oldest beyond the cap.
 *
 * Run on read rather than on a timer: the only moment the size matters is when
 * something is about to be added, and a timer would need owning by a component
 * that has no other reason to exist.
 */
function prune(store: Store): Store {
  const cutoff = Date.now() - MAX_AGE_MS;
  const live = Object.entries(store).filter(
    ([, draft]) => typeof draft?.savedAt === "number" && draft.savedAt > cutoff,
  );

  live.sort(([, a], [, b]) => b.savedAt - a.savedAt);
  return Object.fromEntries(live.slice(0, MAX_DRAFTS));
}

export function loadDraft(ticketId: number): Draft | null {
  const store = prune(read());
  const draft = store[String(ticketId)];
  if (!draft) return null;

  /*
   * Normalise on the way out. Drafts written by an earlier build are sitting in
   * real browsers right now, where `cc` was a comma-separated string and
   * `uploads` did not exist -- reading one of those without this would put a
   * string where the composer expects an array and blank the reply it was
   * supposed to be restoring.
   */
  return {
    ...draft,
    cc: Array.isArray(draft.cc) ? draft.cc : [],
    uploads: Array.isArray(draft.uploads) ? draft.uploads : [],
    /* Same normalisation, for every draft written before forwarding carried
       its files -- which is every draft sitting in a browser today. */
    carried: Array.isArray(draft.carried) ? draft.carried : [],
    /* Null, not undefined, for a draft written before forwarding existed:
       "this is not a forward" is the answer, and it is a definite one. */
    forwardTo: Array.isArray(draft.forwardTo) ? draft.forwardTo : null,
    replyTo: Array.isArray(draft.replyTo) ? draft.replyTo : null,
  };
}

/**
 * Save, or clear when there is nothing worth keeping.
 *
 * An empty body clears rather than storing a blank: otherwise deleting what you
 * wrote would leave a draft that restores as an empty editor and, worse, keeps
 * the "unsent draft" marker on the ticket forever.
 *
 * Attached files count as content in their own right. Picking a file is work,
 * and an agent who attaches a quote before writing anything has still done
 * something worth coming back to.
 */
export function saveDraft(
  ticketId: number,
  draft: Omit<Draft, "savedAt">,
): void {
  const store = prune(read());
  const key = String(ticketId);

  if (
    !hasContent(draft.html) &&
    draft.uploads.length === 0 &&
    draft.carried.length === 0
  ) {
    delete store[key];
  } else {
    store[key] = { ...draft, savedAt: Date.now() };
  }

  write(store);
  /*
   * And say so. This was missing, and the symptom was that the Drafts row
   * never appeared: the store is read through `useSyncExternalStore`, which
   * has no way to know localStorage moved underneath it, so a draft written
   * here was saved correctly, restored correctly, and invisible in the
   * sidebar until the next full page load. `clearDraft` and both new-ticket
   * writers always notified, which is why the row could vanish but never
   * arrive.
   *
   * Cheap to call on every keystroke's worth of debounce: `notify` recomputes
   * a short signature and returns without touching a listener unless the set
   * of drafts actually changed, which after the first character it has not.
   *
   * It belongs here and in the other mutators rather than inside `write`,
   * because `listDrafts` writes too -- it prunes on the way out -- and that
   * one runs while the Drafts view is rendering. Notifying from there would
   * be a store telling React it changed in the middle of reading it.
   */
  notify();
}

/** A reply draft, with the ticket it belongs to. */
export interface DraftEntry {
  ticketId: number;
  draft: Draft;
}

/**
 * Every reply draft this browser is holding, newest first.
 *
 * Pruned on the way out like every other read, so the list can never offer a
 * draft that has aged past the point where loading it would find nothing.
 */
export function listDrafts(): DraftEntry[] {
  const store = prune(read());
  write(store);

  return Object.entries(store)
    .map(([key, draft]) => ({ ticketId: Number(key), draft }))
    /* A key that is not a number cannot name a ticket. Nothing writes one
       today, but this store is a decade of browsers and shrugging is cheaper
       than a row that navigates to /v/open/NaN. */
    .filter((entry) => Number.isInteger(entry.ticketId))
    .sort((a, b) => b.draft.savedAt - a.draft.savedAt);
}

export function clearDraft(ticketId: number): void {
  const store = prune(read());
  delete store[String(ticketId)];
  write(store);
  notify();
}

/**
 * Whether a body is worth keeping.
 *
 * contenteditable leaves `<br>` or an empty `<div>` behind when the last
 * character is deleted, so a naive length check would treat a cleared composer
 * as a draft and never let the marker go.
 */
export function hasContent(html: string): boolean {
  return (
    html
      .replace(/<[^>]+>/g, "")
      .replace(/&nbsp;/g, " ")
      .trim().length > 0
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Tickets that were being raised and never sent.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Tickets somebody started raising and never sent.
 *
 * Separate from the store above because it is a different kind of thing: a
 * reply draft belongs to a conversation that exists and is keyed by it, where
 * this belongs to nothing yet. Keying it under a ticket id it does not have
 * would have meant inventing one, so these get an id of their own.
 *
 * **Several at once, keyed, where this used to be one slot.** The old reasoning
 * was that the dialog is a modal, so only one can be written at a time and a
 * second slot was a promise the UI could not keep. True about the writing and
 * wrong about the keeping: an agent half way through raising a ticket who is
 * interrupted by something urgent has to abandon one to start the other, which
 * is the exact loss the whole store exists to prevent. The modal is still one
 * at a time; what is kept is not.
 *
 * The id is minted when a dialog opens and travels with it, so resuming writes
 * back to the same entry rather than to "the" draft.
 */
const NEW_TICKET_KEY = "helpdesk.newTicketDrafts";

/** Where the single draft lived before this was a list. Read once, then dropped. */
const LEGACY_NEW_TICKET_KEY = "helpdesk.newTicketDraft";

/** Unsent new tickets kept at once -- a bound on the store, not a workflow limit. */
const MAX_NEW_TICKETS = 20;

type NewTicketStore = Record<string, NewTicketDraft>;

/**
 * A fresh id for a dialog that is about to open.
 *
 * `crypto.randomUUID` where it exists, which is everywhere this app runs, with
 * a fallback that only has to be unique within one browser's store rather than
 * globally.
 */
export function newTicketDraftId(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `d${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
  }
}

export interface NewTicketDraft {
  to: { email: string; name?: string }[];
  /** A name typed for a recipient the CRM has never seen. */
  toName: string;
  subject: string;
  /** Kept as the strings the form holds, so restoring is an assignment. */
  pipelineId: string;
  channelId: string;
  cc: { email: string; name?: string }[];
  showCc: boolean;
  body: string;
  uploads: {
    storageKey: string;
    filename: string;
    mimeType: string;
    sizeBytes: number;
  }[];
  orderNumber: string;
  quoteNumber: string;
  returnsReference: string;
  laneTask: string;
  savedAt: number;
}

/**
 * Every unsent new ticket, newest first, with the id each is keyed by.
 *
 * Reads the legacy single-slot draft on the way past and folds it in, so an
 * agent who had one in flight when this shipped finds it in the list rather
 * than losing it to a rename.
 */
export function listNewTicketDrafts(): { id: string; draft: NewTicketDraft }[] {
  const store = pruneNewTickets(readNewTickets());
  writeNewTickets(store);

  return Object.entries(store)
    .map(([id, draft]) => ({ id, draft }))
    .sort((a, b) => b.draft.savedAt - a.draft.savedAt);
}

export function loadNewTicketDraft(id: string): NewTicketDraft | null {
  return pruneNewTickets(readNewTickets())[id] ?? null;
}

/**
 * Save, or clear when there is nothing worth keeping.
 *
 * "Worth keeping" is deliberately generous: a recipient on their own, or a
 * subject on its own, is somebody who started and was interrupted, which is
 * the entire case this exists for. What it excludes is the dialog being opened
 * and closed again, which must not leave a draft behind -- otherwise the
 * sidebar grows a permanent "1" that nothing the agent does will clear.
 *
 * The pipeline and the From alias do not count. Both are pre-filled from the
 * agent's own defaults the moment the dialog opens, so counting them would
 * make every accidental open a draft.
 */
export function saveNewTicketDraft(
  id: string,
  draft: Omit<NewTicketDraft, "savedAt">,
): void {
  const worthKeeping =
    draft.to.length > 0 ||
    draft.cc.length > 0 ||
    draft.subject.trim().length > 0 ||
    draft.uploads.length > 0 ||
    hasContent(draft.body);

  if (!worthKeeping) {
    clearNewTicketDraft(id);
    return;
  }

  const store = pruneNewTickets(readNewTickets());
  store[id] = { ...draft, savedAt: Date.now() };
  writeNewTickets(store);
  notify();
}

export function clearNewTicketDraft(id: string): void {
  const store = pruneNewTickets(readNewTickets());
  delete store[id];
  writeNewTickets(store);
  notify();
}

function readNewTickets(): NewTicketStore {
  const store: NewTicketStore = {};

  try {
    const raw = window.localStorage.getItem(NEW_TICKET_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      Object.assign(store, parsed as NewTicketStore);
    }
  } catch {
    // An unreadable store is an empty one. Never throw on the typing path.
  }

  /*
   * The one that was here before this was a list. Folded in rather than
   * migrated in place, so this stays a pure read -- the write that follows any
   * caller's change is what actually persists it under its new id, and the old
   * key is dropped then.
   */
  try {
    const legacy = window.localStorage.getItem(LEGACY_NEW_TICKET_KEY);
    if (legacy) {
      const draft = JSON.parse(legacy) as NewTicketDraft;
      if (typeof draft?.savedAt === "number") store.legacy = draft;
    }
  } catch {
    // Same.
  }

  return store;
}

function writeNewTickets(store: NewTicketStore): void {
  try {
    window.localStorage.setItem(NEW_TICKET_KEY, JSON.stringify(store));
    /* Whatever the legacy slot held is in the store above by now, so leaving
       it would resurrect it on the next read as a second copy. */
    window.localStorage.removeItem(LEGACY_NEW_TICKET_KEY);
  } catch {
    // A full or disabled localStorage must not break composing.
  }
}

/**
 * Drop what has aged out, and hold the store to its ceiling.
 *
 * Same clock as a reply draft: a ticket somebody started raising a fortnight
 * ago is not one they are coming back to. Over the ceiling, the oldest go --
 * the newest are the ones somebody is still thinking about.
 */
function pruneNewTickets(store: NewTicketStore): NewTicketStore {
  const cutoff = Date.now() - MAX_AGE_MS;

  const kept = Object.entries(store)
    .filter(([, draft]) => typeof draft?.savedAt === "number" && draft.savedAt >= cutoff)
    .map(([id, draft]): [string, NewTicketDraft] => [
      id,
      {
        ...draft,
        to: Array.isArray(draft.to) ? draft.to : [],
        cc: Array.isArray(draft.cc) ? draft.cc : [],
        uploads: Array.isArray(draft.uploads) ? draft.uploads : [],
      },
    ])
    .sort((a, b) => b[1].savedAt - a[1].savedAt)
    .slice(0, MAX_NEW_TICKETS);

  return Object.fromEntries(kept);
}

// ─────────────────────────────────────────────────────────────────────────────
// Watching the store.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Why this exists rather than re-reading on navigation.
 *
 * The sidebar's count and the Drafts view are on screen while the thing they
 * describe changes underneath them -- an agent raising a new ticket from the
 * "+" button is looking at the Drafts list behind the dialog. localStorage
 * fires no event for the tab that wrote it, so there is nothing to subscribe
 * to and the first version simply recounted whenever the URL changed. That is
 * wrong in exactly the case above, where nothing navigates.
 *
 * ## Why a signature and not a counter
 *
 * A draft is saved on a debounce as somebody types, so a plain "something
 * changed" tick would fire every few hundred milliseconds -- and the Drafts
 * view turns its ids into a query key, so each tick would be a refetch of a
 * list that has not changed. The signature is what the subscribers actually
 * care about: which tickets have a draft, and how fresh the unsent new ticket
 * is. Typing into an existing reply does not move it, so nothing re-renders.
 */
const listeners = new Set<() => void>();

/** Cached so `getSnapshot` is O(1) -- React calls it on every render. */
let signature: string | null = null;

function computeSignature(): string {
  const ids = Object.keys(read())
    .map(Number)
    .filter((id) => Number.isInteger(id))
    .sort((a, b) => a - b);

  /*
   * The unsent new tickets, as "id:savedAt" pairs. The stamps are in it so a
   * draft being typed into still moves the signature -- the Drafts view shows
   * these rows and has to re-render as a subject appears in one -- while the
   * reply-draft half stays stable, which is what keeps that list's query key
   * from turning over on every keystroke.
   */
  const unsent = Object.entries(readNewTickets())
    .map(([id, draft]) => `${id}:${draft?.savedAt ?? ""}`)
    .sort();

  return `${ids.join(",")}|${unsent.join(",")}`;
}

function getSignature(): string {
  if (signature === null) signature = computeSignature();
  return signature;
}

function notify(): void {
  const next = computeSignature();
  if (next === signature) return;
  signature = next;
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function useDraftSignature(): string {
  return useSyncExternalStore(subscribe, getSignature, getSignature);
}

/**
 * The tickets with an unsent reply, for the Drafts view to ask the server
 * about. Stable while the set is unchanged, so it can be a query key.
 */
export function useDraftIds(): number[] {
  const ids = useDraftSignature().split("|")[0];
  return useMemo(() => (ids ? ids.split(",").map(Number) : []), [ids]);
}

/** How many unsent drafts there are, of both kinds. The sidebar's badge. */
export function useDraftCount(): number {
  const signature = useDraftSignature();
  return useMemo(() => {
    const [ids, unsent] = signature.split("|");
    return (
      (ids ? ids.split(",").length : 0) + (unsent ? unsent.split(",").length : 0)
    );
  }, [signature]);
}

/** The unsent new tickets, kept current as they are written. */
export function useUnsentNewTickets(): { id: string; draft: NewTicketDraft }[] {
  const signature = useDraftSignature();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- the signature is
  // the dependency; the loader reads the store the signature summarises.
  return useMemo(() => listNewTicketDrafts(), [signature]);
}
